import { Dispatch, SetStateAction, useMemo } from 'react'
import { Sidebar } from 'primereact/sidebar'
import { useTranslation } from 'react-i18next'

// local imports
import { IFilteredGymGroupList, ISaveGroupLessonReservation } from 'types/GymType'
import useSaveGroupLessonReservation from '../../hooks/use-save-group-reservation'
import {
  ContentArea,
  Header,
  ImagePreview,
  InfoList,
  InfoListItem,
  ReservationSummary,
  SidebarActionArea,
  SidebarConfirmButton,
  SidebarContainer,
} from '../styled'

/* assets */
import calendarr from 'assets/icons/calendar-gray.svg'
import time from 'assets/icons/time.svg'
import location from 'assets/icons/location-marker-gray.svg'
import building from 'assets/icons/bina-gray.svg'
import gym from 'assets/icons/gym-gray.svg'
import creditCard from 'assets/icons/credit-card.svg'
import avatar from 'assets/icons/avatar.svg'
import resIcon from 'assets/icons/gym-res.svg'

interface ScheduleSidebarProps {
  closeAfterSave: () => void
  visible: boolean
  setVisible: Dispatch<SetStateAction<boolean>>
  groupLessonInfo: IFilteredGymGroupList
  onCalendar?: boolean
  closeSideSheet?: () => void
}

export default function GroupLessonReservationSidebar({
  groupLessonInfo,
  visible,
  setVisible,
  closeAfterSave,
  onCalendar = false,
  closeSideSheet,
}: ScheduleSidebarProps) {
  const { t } = useTranslation()

  // Gym Group Lesson - Gym Salon information desctructuring
  const {
    id,
    /* ---- */
    lessonDateStr,
    startHourStr,
    endHourStr,
    priceStr,
    price,
    gymLessonType: { name: lessonName },
    gymTrainer: { fullName },
    floor: { name: floorName, locationName, districtName, cityName, countryName, blockName },
  } = groupLessonInfo || {}
  const appointmentTime = `${startHourStr} - ${endHourStr}`
  const salonLocationName = `${locationName}, ${districtName}, ${cityName}, ${countryName} `
  const priceVal = !!price ? priceStr : t('most-used-words.price-free')
  /* Handle Save Gym Group Lesson Reservation | Custom Hook */
  const resInfo: ISaveGroupLessonReservation = { gymGroupLessonId: id }
  const { loading, saveGroupLessonReservation } = useSaveGroupLessonReservation(resInfo, closeAfterSave)

  const reservationInfoItems = useMemo(
    () => [
      {
        id: 'lesson-name',
        icon: gym,
        title: t('gym-res-modal.lesson-name'),
        value: lessonName,
      },
      {
        id: 'trainer-name',
        icon: avatar,
        title: t('gym-res-modal.trainer-name'),
        value: fullName,
      },
      {
        id: 'price-info',
        icon: creditCard,
        title: t('gym-res-modal.price-info'),
        value: priceVal,
      },
      {
        id: 'location-info',
        icon: location,
        title: t('gym-res-modal.salon-location'),
        value: salonLocationName,
      },
      {
        id: 'block-floor-info',
        icon: building,
        title: t('gym-res-modal.block-floor'),
        value: `${blockName} / ${floorName}`,
      },
      {
        id: 'date-info',
        icon: calendarr,
        title: t('gym-res-modal.reservation-date'),
        value: lessonDateStr,
      },
      {
        id: 'time-info',
        icon: time,
        title: t('gym-res-modal.reservation-time'),
        value: appointmentTime,
      },
    ],
    [blockName, floorName, fullName, lessonDateStr, lessonName, appointmentTime, priceVal, salonLocationName, t]
  )

  const closeSidebar = () => (!onCalendar ? setVisible(false) : closeSideSheet?.())

  return (
    <Sidebar
      style={{ width: '500px' }}
      className="of-bordered-sidebar-box"
      visible={visible}
      position="right"
      onHide={closeSidebar}
      dismissable={false}
      closeOnEscape={false}>
      <SidebarContainer>
        <ContentArea>
          <Header>{t('gym-res-modal.gym-reservation')}</Header>
          <ReservationSummary>{t('gym-res-modal.reservation-summary')}</ReservationSummary>
          <ImagePreview url={resIcon} />
          <InfoList>
            <div className="reservation-subtitle">{t('gym-res-modal.reservation-info')}</div>
            {reservationInfoItems.map((info) => (
              <InfoListItem key={info.id}>
                <img src={info.icon} alt={info.id} />
                <span className="reservation-info-title"> {info.title} : </span>
                <span className="reservation-info-value">{info.value}</span>
              </InfoListItem>
            ))}
          </InfoList>
        </ContentArea>
        {/* Action Buttons */}
        <SidebarActionArea>
          <SidebarConfirmButton
            id="saveGroupLessonReservation"
            loading={loading}
            label={t('buttons.confirm')}
            onClick={saveGroupLessonReservation}
          />
        </SidebarActionArea>
      </SidebarContainer>
    </Sidebar>
  )
}
